import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Observable, Subscription } from "rxjs";
import { DEVICE_VCT_AUX_LIST } from "src/app/conf/devices-vct";
import { ModuleVct, Plant } from "src/app/model/device-vct/device-vct-implant";
import { StandardNewValidator, StandardPageLayout } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";
import { AppState } from "src/app/store/app.reducer";
import * as DeviceVctActions from 'src/app/store/device-vct/device-vct.actions';
import { selectDeviceVctModuleEdited, selectDeviceVctModuleEditing, selectVctPlantsList } from "src/app/store/device-vct/device-vct.selector";

@Component({
	templateUrl: './aux-config-device-vct.dialog.component.html',
})

export class AuxConfigDeviceVctDialogComponent implements OnInit, OnDestroy {
	editDeviceForm: FormGroup;

	moduleId: number;
	module: ModuleVct;

	plantsList$: Observable<Plant[] | undefined>;
	plantsListSub: Subscription | undefined;
	deviceVctAuxConfigEditing$: Observable<boolean | undefined>;
	deviceVctAuxConfigEdited$: Observable<boolean | undefined>;
	deviceVctAuxConfigEditedSub: Subscription;
	auxToggleResultSub: Subscription | undefined;

	/** Stato aux considerato aggiornato dopo dispatch (ottimistico) / success. */
	private auxEnabledState = new Map<number, boolean>();

	auxList: any;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private dialogRef: DynamicDialogRef,
		private formBuilder: FormBuilder,
		private actions$: Actions,
		public config: DynamicDialogConfig
	) {
		if (config.data !== undefined && this.config.data.moduleId !== undefined) {
			this.moduleId = this.config.data.moduleId;
		} else {
			this.moduleId = undefined;
		}
		this.initSelectors();
	}

	initSelectors(): void {
		this.deviceVctAuxConfigEditing$ = this.store.select(selectDeviceVctModuleEditing);
		this.deviceVctAuxConfigEdited$ = this.store.select(selectDeviceVctModuleEdited);
		this.plantsList$ = this.store.select(selectVctPlantsList);
	}

	ngOnInit(): void {
		this.auxList = DEVICE_VCT_AUX_LIST;
		this.subscribeAll();
		this.onResize();

		StandardNewValidator.setFirstCompile(true);
	}

	ngOnDestroy(): void {
		this.deviceVctAuxConfigEditedSub?.unsubscribe();
		this.plantsListSub?.unsubscribe();
		this.auxToggleResultSub?.unsubscribe();
	}

	buildForm(): void {
		this.editDeviceForm = this.formBuilder.group(this.buildFormAux());
	}

	buildFormAux() {
		const res = {};
		this.auxList.forEach((aux) => {
			res[aux.value] = [this.isSelectedAux(aux.value), [Validators.required]];
		});
		return res;
	}

	subscribeAll(): void {
		this.plantsListSub = this.plantsList$?.subscribe(plantsList => {
			if (plantsList !== undefined && plantsList.length > 0) {
				for (const device of plantsList) {
					if (device !== undefined && device.Modules !== undefined && device.Modules.length > 0) {
						for (const module of device.Modules) {
							if (module.Id !== undefined && module.Id === this.moduleId) {
								this.module = module;
								if (!this.editDeviceForm) {
									this.syncAuxEnabledStateFromModule();
									this.buildForm();
								}
							}
						}
					}
				}
			}
		});

		this.auxToggleResultSub = this.actions$.pipe(
			ofType(
				DeviceVctActions.enableVctAuxSuccess,
				DeviceVctActions.disableVctAuxSuccess,
				DeviceVctActions.enableVctAuxFailed,
				DeviceVctActions.disableVctAuxFailed,
			)
		).subscribe((action) => {
			if (!this.module || action.moduleId !== this.module.Id || !this.editDeviceForm?.controls[action.auxId]) {
				return;
			}

			if (action.type === DeviceVctActions.enableVctAuxSuccess.type) {
				this.auxEnabledState.set(action.auxId, true);
				return;
			}
			if (action.type === DeviceVctActions.disableVctAuxSuccess.type) {
				this.auxEnabledState.set(action.auxId, false);
				return;
			}

			const enabled = action.type === DeviceVctActions.disableVctAuxFailed.type;
			this.auxEnabledState.set(action.auxId, enabled);
			this.f[action.auxId].setValue(enabled, { emitEvent: false });
		});
	}


	private syncAuxEnabledStateFromModule(): void {
		this.auxEnabledState.clear();
		this.auxList?.forEach((aux) => {
			this.auxEnabledState.set(aux.value, this.isSelectedAuxFromModule(aux.value));
		});
	}

	private isSelectedAuxFromModule(auxId: number): boolean {
		if (auxId !== undefined && this.module?.Features?.length > 0) {
			return this.module.Features.some(feature => feature.FeatureId === auxId);
		}
		return false;
	}

	isSelectedAux(auxId: number): boolean {
		if (this.auxEnabledState.has(auxId)) {
			return this.auxEnabledState.get(auxId);
		}
		return this.isSelectedAuxFromModule(auxId);
	}

	get f(): any {
		return this.editDeviceForm.controls;
	}

	closeDialog(): void {
		const somethingIsChanged: boolean = !this.editDeviceForm.pristine;

		this.dialogRef.close(somethingIsChanged);
	}

	save(auxId: number): void {
		if (this.module !== undefined && this.isSelectedAux(auxId) && !this.f[auxId].value) {
			this.auxEnabledState.set(auxId, false);
			this.store.dispatch(DeviceVctActions.disableVctAux({ moduleId: this.module.Id, auxId: auxId }));
		} else if (this.module !== undefined && !this.isSelectedAux(auxId) && this.f[auxId].value) {
			this.auxEnabledState.set(auxId, true);
			this.store.dispatch(DeviceVctActions.enableVctAux({ moduleId: this.module.Id, auxId: auxId }));
		}
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	newShowErrors(type: AuxConfigDeviceVctDialogComponent['f'], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: AuxConfigDeviceVctDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	dynamicInputTextFormat(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}
}
